/**
 * Public technology / university pages — the API, in one place.
 *
 * GET /api/v1/pages/by-path resolves any URL the SPA does not route itself:
 * a technology page, a university page, a redirect from an old URL, or
 * nothing. The server decides what the caller may see (pre-login, post-login
 * or paid) from the bearer token, and a locked section arrives without its
 * data — the page only ever renders what the response carries.
 *
 * Kept out of services/apiService.ts (same base URL and stored session): the
 * endpoints are public, the bearer is optional, and a 404 here is an answer
 * ("no such page"), not a failure.
 */

import { apiService } from '../../../services/apiService';
import { currentIds } from '../../../utils/visitorTracking';

export const API_BASE = (): string => apiService.getApiBaseUrl();

// ---------------------------------------------------------------------------
// Shapes (the contract's TechPage / UniversityPage)
// ---------------------------------------------------------------------------

export type PageState = 'pre_login' | 'post_login' | 'paid';
export const PAGE_STATES: PageState[] = ['pre_login', 'post_login', 'paid'];

export type SectionId =
  | 'summary' | 'trl' | 'value_levers' | 'researchers_ip' | 'problem_applications'
  | 'university_details' | 'industry_insights' | 'similar_tech';

export type SectionMode = 'visible' | 'locked' | 'hidden' | 'short' | 'full';

export interface SectionLock {
  reason: 'sign_in' | 'upgrade';
  message?: string;
}

export interface PageSection {
  id: SectionId | string;
  mode: SectionMode;
  lock?: SectionLock;
  data?: any;
}

export interface SummaryData { text: string; is_short: boolean }
/** TRL provenance buckets (Backend admin_trl_provenance.categorize). */
export type TrlCategory =
  | 'disclosed_confirmed' | 'disclosed_mismatch' | 'disclosed_no_evidence' | 'inferred' | 'not_disclosed_no_evidence';
/** tech_pages.trl_block: `title` ("TRL 6" / "TRL not found") is also sent as
 *  `label`, `note` is the provenance category's wording, `pill` the short
 *  form ("TRL 6" / "TRL —"). title / pill / category are absent from older servers. */
export interface TrlData {
  level: number | null;
  label: string;
  title?: string | null;
  description?: string | null;
  note?: string | null;
  pill?: string | null;
  category?: TrlCategory | string | null;
}
export interface ValueLeversData { items: Array<string | { lever?: string; tag?: string; label?: string }>; placeholder?: boolean }
export interface PatentRow { number?: string | null; type?: string | null; country?: string | null }
export interface ResearchersIpData {
  researchers?: string[];
  ip_summary?: string | null;
  publications_summary?: string | null;
  patents?: PatentRow[];
}
export interface ProblemApplicationsData {
  problem_addressed?: string | null;
  problem_evidence?: string | null;
  example_applications?: string[];
  placeholder?: boolean;
}
export interface UniversityDetailsData { name: string; label?: string | null; country?: string | null; path?: string | null }
export interface IndustryInsightsData { industry?: string | null; count?: number | null; placeholder?: boolean }
export interface SimilarItem { id?: string; title: string; path: string; university_label?: string | null }
export interface SimilarTechData { items: SimilarItem[]; placeholder?: boolean }

export interface Breadcrumb { label: string; path: string }

export interface PageSeo {
  title: string;
  description: string;
  canonical: string;
  robots?: string;
  og?: { title?: string; description?: string; url?: string; type?: string; image?: string | null };
  json_ld?: any[];
}

export interface AdminPreview { allowed: boolean; states: PageState[] }

/** How the page enters after an in-site navigation (similar tech, a
 *  university's tech cards). */
export interface PageTransition {
  enabled: boolean;
  effect: 'fade' | 'fade-up' | 'slide' | 'none';
  duration_ms: number;
  min_loader_ms: number;
  loader: 'spinner' | 'logo' | 'bar' | 'none';
}

export const DEFAULT_TRANSITION: PageTransition = {
  enabled: true, effect: 'fade-up', duration_ms: 320, min_loader_ms: 350, loader: 'spinner',
};

export interface TechPageData {
  state: PageState;
  id: string;
  path: string;
  canonical_url: string;
  title: string;
  title_accent_from?: number;
  university: { code: string; name: string; label?: string | null; country?: string | null; path?: string | null };
  breadcrumbs: Breadcrumb[];
  sections: PageSection[];
  ctas: { contact_university: boolean; ip_assessment: boolean; share: boolean; bookmark: boolean };
  bookmarked?: boolean;
  seo: PageSeo;
  admin_preview?: AdminPreview;
  /** Admin → Tech URL pages → Sections & access → Page transition. */
  ui?: { transition?: PageTransition; breadcrumbs?: boolean };
}

export interface UniversityTechRow {
  id: string;
  title: string;
  path: string;
  trl_level?: number | null;
  industry?: string | null;
}

export interface UniversityPageData {
  state: PageState;
  code: string;
  path: string;
  canonical_url: string;
  name: string;
  label?: string | null;
  country?: string | null;
  portal_url?: string | null;
  tto_name?: string | null;
  stats: { technologies: number; researchers?: number | null; industries: Array<{ name: string; n: number }> };
  techs: { rows: UniversityTechRow[]; total: number; page: number; pages: number; per_page: number };
  filters_applied?: { q?: string | null; industry?: string | null; trl?: number | null } | null;
  breadcrumbs: Breadcrumb[];
  seo: PageSeo;
  admin_preview?: AdminPreview;
  /** Admin → Tech URL pages → Sections & access → Page transition. */
  ui?: { transition?: PageTransition; breadcrumbs?: boolean };
}

/** University-page list parameters (by-path passes them through). */
export interface ListParams {
  q?: string;
  industry?: string;
  trl?: number;
  /** 0-based, as the API takes it. */
  page?: number;
}

export type ByPathResult =
  | { kind: 'tech'; page: TechPageData }
  | { kind: 'university'; page: UniversityPageData }
  | { kind: 'redirect'; to: string }
  | { kind: 'not_found' }
  | { kind: 'rate_limited' }
  | { kind: 'error'; status: number; message: string };

// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------

export const storedUserId = (): string => {
  try { return apiService.getStoredUserId?.() || ''; } catch { return ''; }
};

const storedToken = (): string => {
  try { return apiService.getStoredAccessToken?.() || ''; } catch { return ''; }
};

/** A stored session (user id + token). Whether it still verifies is the server's call. */
export const hasStoredSession = (): boolean => !!storedUserId() && !!storedToken();

const bearer = (): Record<string, string> => {
  const t = storedToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * Renew the access token once, through apiService's own single-flight
 * refresh (the backend overwrites the token in place, so two refreshes in
 * parallel would invalidate each other). Resolves to whether a new token was
 * stored.
 */
export const refreshSession = async (): Promise<boolean> => {
  try {
    const fn = (apiService as any).refreshAccessToken;
    if (typeof fn !== 'function') return false;
    const t = await fn.call(apiService);
    return !!t;
  } catch { return false; }
};

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

async function getJson(url: string, init: RequestInit): Promise<{ status: number; body: any }> {
  const res = await fetch(url, { ...init, mode: 'cors', credentials: 'omit' });
  const body = await res.json().catch(() => ({} as any));
  return { status: res.status, body };
}

const errText = (body: any, status: number): string => {
  const d = body?.detail ?? body?.error;
  if (typeof d === 'string' && d) return d;
  if (d && typeof d === 'object') return String(d.message || d.error || `Request failed (${status}).`);
  return `Request failed (${status}).`;
};

/** sessionStorage flag: the next page fetch re-reads the viewer's plan. */
export const FRESH_VIEWER_KEY = 'utech_tp_fresh_viewer';

export async function fetchByPath(
  path: string,
  opts: { previewState?: PageState | null; list?: ListParams; signal?: AbortSignal; share?: string | null } = {},
): Promise<ByPathResult> {
  const p = new URLSearchParams();
  p.set('path', path);
  // A tracked share link's code (?s= on the opened URL): the server records the open.
  if (opts.share) {
    p.set('share', opts.share);
    // …and ties the open to this visit, so a later sign-in / sign-up counts for it.
    const ids = visitorIds();
    if (ids.visitor_id) p.set('visitor_id', ids.visitor_id);
    if (ids.session_id) p.set('session_id', ids.session_id);
  }
  const uid = storedUserId();
  const auth = uid ? bearer() : {};
  if (uid) p.set('user_id', uid);
  // Set by the plans sheet just before it reloads the page after a payment:
  // this one fetch re-reads the plan instead of the server's 60 s cache.
  try {
    if (uid && sessionStorage.getItem(FRESH_VIEWER_KEY)) {
      sessionStorage.removeItem(FRESH_VIEWER_KEY);
      p.set('fresh', '1');
    }
  } catch { /* storage off */ }
  if (opts.previewState) p.set('preview_state', opts.previewState);
  const l = opts.list || {};
  if (l.q) p.set('q', l.q.slice(0, 100));
  if (l.industry) p.set('industry', l.industry);
  if (l.trl) p.set('trl', String(l.trl));
  if (l.page) p.set('page', String(l.page));
  try {
    const { status, body } = await getJson(`${API_BASE()}/api/v1/pages/by-path?${p}`, {
      headers: { Accept: 'application/json', ...auth },
      signal: opts.signal,
    });
    const data = body?.data;
    if (status === 429 || body?.error === 'rate_limited') return { kind: 'rate_limited' };
    if (status === 404 || data?.type === 'not_found') return { kind: 'not_found' };
    if (status >= 200 && status < 300 && data) {
      if (data.type === 'redirect' && typeof data.to === 'string') return { kind: 'redirect', to: data.to };
      if (data.type === 'tech' && data.page) return { kind: 'tech', page: data.page as TechPageData };
      if (data.type === 'university' && data.page) return { kind: 'university', page: data.page as UniversityPageData };
    }
    return { kind: 'error', status, message: errText(body, status) };
  } catch (e: any) {
    if (e?.name === 'AbortError') throw e;
    return { kind: 'error', status: 0, message: 'Network error — check your connection and try again.' };
  }
}

/** Bookmark / un-bookmark a technology for the signed-in visitor. */
export async function setBookmark(techId: string, on: boolean): Promise<boolean> {
  const uid = storedUserId();
  if (!uid) return false;
  try {
    const { status } = await getJson(`${API_BASE()}/api/v1/saved?user_id=${encodeURIComponent(uid)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...bearer() },
      body: JSON.stringify({ asset_type: 'technology', asset_id: techId, bookmarked: on, liked: null, linked_chat_id: null }),
    });
    return status >= 200 && status < 300;
  } catch { return false; }
}

// ---------------------------------------------------------------------------
// "Get your university listed" (POST /api/v1/universities/listing-request)
// ---------------------------------------------------------------------------

export type TechCountRange = '<10' | '10-50' | '50-200' | '200-1000' | '1000+';
export const TECH_COUNT_RANGES: TechCountRange[] = ['<10', '10-50', '50-200', '200-1000', '1000+'];

export type ListingField =
  | 'institution' | 'website' | 'country' | 'contact_name' | 'role' | 'email'
  | 'phone' | 'tech_count_range' | 'tto_portal_url' | 'message';

export interface ListingRequestBody {
  institution: string;
  website: string;
  country: string;
  contact_name: string;
  role?: string;
  email: string;
  phone?: string;
  tech_count_range?: TechCountRange;
  tto_portal_url?: string;
  message?: string;
  /** Honeypot — always empty from a real visitor. */
  website_hp: string;
  page_url?: string;
  visitor_id?: string;
  session_id?: string;
}

export interface ListingResult {
  ok: boolean;
  ref: string;
  error: string;
  field?: ListingField;
  status: number;
}

export async function submitListingRequest(body: ListingRequestBody): Promise<ListingResult> {
  try {
    const { status, body: r } = await getJson(`${API_BASE()}/api/v1/universities/listing-request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (status >= 200 && status < 300 && r?.success !== false) {
      return { ok: true, ref: String(r?.data?.ref || ''), error: '', status };
    }
    // {success:false, error, field}; tolerate FastAPI's {detail: {...}} too.
    const d = r?.detail && typeof r.detail === 'object' && !Array.isArray(r.detail) ? r.detail : r;
    const error = status === 429 ? 'rate_limited' : String(d?.error || d?.message || (typeof r?.detail === 'string' ? r.detail : '') || `Request failed (${status}).`);
    return { ok: false, ref: '', error, field: d?.field || undefined, status };
  } catch {
    return { ok: false, ref: '', error: 'Network error — check your connection and try again.', status: 0 };
  }
}

/** The site's own visitor/session ids (utils/visitorTracking), so a listing
 *  request can be joined to the visit that sent it. */
export const visitorIds = (): { visitor_id?: string; session_id?: string } => {
  try { return currentIds(); } catch { return {}; }
};
